import { Controller } from "@hotwired/stimulus"
import { timerUnit } from "../helpers/locale"

export default class extends Controller {
  static targets = [
                    "slide", "progressFill", "bgGradient", "navRow", "nextBtn", "nextLabel",
                    "backBtn", "nameInput", "currencySelect", "timezoneSelect", "tierCard",
                    "tierTotal", "categoryAmount", "currencyLabel", "ruleCard", "manualToggle",
                    "manualForm", "manualNameInput", "manualLimitInput", "reminderEnableWrap",
                    "reminderEnableBtn", "reminderDenied", "reminderEnabledWrap", "goalChip",
                    "goalWrap", "userNameCell", "greetText", "waysDemo", "waysField", "waysFieldBox",
                    "waysTyped", "waysCaret", "waysPlaceholder", "waysCount", "waysPhotoRow",
                    "waysTakePhoto", "waysRings", "waysMascotOff", "waysMascotOn",
                    "waysStatus", "waysTimer", "waysLoading", "waysAnalyzing", "waysCard",
                    "waysStrings", "helpIntentWrap", "helpIntentChip", "amountInput",
                    "amountCurrency", "amountSlider", "amountTrack", "amountFill",
                    "amountGlow", "amountBubble", "amountThumb", "amountTicks", "amountScale",
                    "frameworkAmount", "widgetStage", "widgetCard", "widgetDot", "widgetName",
                    "widgetSize", "widgetLine", "widgetCopy" ]

  // The monthly amount the budget_amount slide collects. Kept in step with
  // OnboardingPersistence::MONTHLY_* — the server clamps to the same window, so
  // a payload that skips the UI cannot fund a budget the slider could not have
  // produced.
  static AMOUNT_MIN     = 500
  static AMOUNT_MAX     = 12000
  static AMOUNT_DEFAULT = 4000
  static AMOUNT_STEP    = 10     // what typing settles onto
  static AMOUNT_DETENT  = 250    // what dragging settles onto

  // Slide 22's widget deck. GoalChrome.fillCurve is an easeOut over 0.9s, and
  // every card in the deck rides that one clock. HOLD is how long a card sits
  // before the deck moves itself on. WATER_* are FYWaterShape's own numbers:
  // amplitude and wavelength in points, sampled down the canvas in 48 steps.
  static WIDGET_FILL_MS      = 900
  static WIDGET_HOLD_MS      = 3600
  static WATER_AMP           = 4.2
  static WATER_WAVELENGTH    = 64
  static WATER_STEPS         = 48

  static values = {
    current: { type: Number, default: 0 },
    highest: { type: Number, default: 0 },
    total: { type: Number, default: 7 },
    csrfToken: String,
    updateUrl: String,
    dashboardUrl: String,
    rtl: { type: Boolean, default: false },
    labelGetStarted: String,
    labelContinue: String,
    labelDashboard: String,
    budgetId: String,
    // Slug the flow should open on. Set by the server only for a lapsed
    // subscriber (straight to "paywall"); empty means start at the beginning.
    startStep: { type: String, default: "" },
    country: { type: String, default: "" },
    currencyMap: { type: Object, default: {} },
    tierData: { type: Object, default: {} },
    // Localized strings passed from the view so JS can build messages per locale
    nextLabels:      { type: Object, default: {} }
  }

  connect() {
    this.touchStartX = 0
    this.selectedTierKey = null
    // Budget framework (slide 14) is pre-selected to the single-budget default
    // (BudgetFramework::DEFAULT_ID) so a zero-tap user still ships a valid
    // budgeting plan.
    this.selectedRule = "single"
    // Seeded rather than gated: the slider always holds a legal amount, so a
    // user who taps straight through still ships a funded plan.
    // Opens at the midpoint of the dial, so the handle sits centred and the
    // range reads as something to move in both directions rather than a floor
    // to push up from.
    this.budgetAmount = this.#clampAmount(
      (this.constructor.AMOUNT_MIN + this.constructor.AMOUNT_MAX) / 2,
      this.constructor.AMOUNT_DETENT
    )
    // Set by the skip link on slide 14. Skipping does not pick "zero" — it
    // withholds the amount entirely, so the budgets are created open-ended and
    // /budgets/assign funds them later, exactly as before this slide existed.
    this.budgetAmountSkipped = false
    this.manualMode = false
    this.notificationsGranted = false
    // Every option screen forces the user to pick — no chip is pre-on, no
    // value is seeded. The flow must engage the user on every reopen.
    this.selectedGoal = null
    // Slide 14 is the only multi-select picker in the flow, so it holds a Set
    // rather than a single key.
    this.selectedHelpIntents = new Set()
    // Slide 9 (habit picker) carries safe seeds — these aren't gated picks
    // (the slide has its own UI defaults) and they feed Slide 10's reveal
    // math even before the user changes them.
    this._onTouchStart = this.handleTouchStart.bind(this)
    this._onTouchEnd = this.handleTouchEnd.bind(this)
    this.element.addEventListener("touchstart", this._onTouchStart, { passive: true })
    this.element.addEventListener("touchend", this._onTouchEnd)

    // Per product decision: every reopen of the app starts the onboarding
    // from scratch — no resume, no restored picks, no skipped questions.
    // Wipe anything left from a previous session so the URL step and
    // window-level payload are the only sources of truth.
    this.clearStorage()

    this.restoreStepFromUrl()
    this.exposeOnboardingPayload()
    // Covers the case where onboarding opens directly on one of these slides
    // (a `?step=` deep link), which never fires the on-entry hook above.
    if (this.currentValue === this.#at("budget_amount")) {
      this.buildAmountRuler()
      this.paintAmount()
    }
    // Same reason as the budget dial: the track reads zero width until its
    // slide is on screen.
    if (this.currentValue === this.#at("widgets")) this.startWidgetDeck()
    this.repriceFrameworks()
  }

  // localStorage is intentionally NOT used to resume onboarding. Per
  // product rule: every reopen of the app starts the flow from scratch.
  // We keep `clearStorage` to wipe any leftover key from earlier
  // versions (or any future write that slips in) — it runs once on
  // connect.
  clearStorage() {
    try { window.localStorage?.removeItem(this.constructor.STORAGE_KEY) } catch (_) { /* no-op */ }
    if (typeof window !== "undefined") window.__onboardingPayload = null
  }

  // Snapshot of the user's typed/picked answers, sourced from instance
  // state. Used both for localStorage and for the server-side
  // OnboardingPersistence payload.
  buildAnswers() {
    const name = this.hasNameInputTarget ? this.nameInputTarget.value.trim() : null
    return {
      name,
      goal:               this.selectedGoal,
      helpIntents:        Array.from(this.selectedHelpIntents),
      tierKey:            this.selectedTierKey,
      budgetAmount:       this.budgetAmountSkipped ? null : this.budgetAmount,
      budgetLimit:        this.budgetLimit,
      budgetCategories:   this.budgetCategories,
      rule:               this.selectedRule
    }
  }

  // Server-shaped payload for OnboardingPersistence. Stamped into
  // window.__onboardingPayload so the auth bridges and the email-fallback
  // form can pick it up without needing a Stimulus reference.
  exposeOnboardingPayload() {
    const a = this.buildAnswers()

    // The budget slides pick a budgeting framework (50/30/20, etc.) and a
    // monthly amount. The server (OnboardingPersistence + BudgetFramework) owns
    // the names, icons and percentages, so the wire payload carries only the
    // rule id and the amount. Either may be missing: the amount is the user's
    // income and is stored on its own even when no framework was picked. It is
    // clamped again server-side, so a doctored payload cannot widen the window.
    let budget = null
    if (a.rule || !this.budgetAmountSkipped) {
      budget = {}
      if (a.rule) budget.rule = a.rule
      if (!this.budgetAmountSkipped) budget.amount = this.budgetAmount
    }

    const currency = this.hasCurrencySelectTarget ? (this.currencySelectTarget.value || null) : null
    const country  = this.hasCountryValue ? (this.countryValue || null) : null
    // OnboardingPersistence has always accepted :timezone; it was simply never
    // sent, so every account fell back to the default zone.
    const timezone = this.hasTimezoneSelectTarget ? (this.timezoneSelectTarget.value || null) : null

    const payload = {
      name:             a.name,
      money_goal:       a.goal,
      help_intents:     a.helpIntents,
      theme:            a.theme,
      currency,
      country,
      timezone,
      budget
    }

    if (typeof window !== "undefined") window.__onboardingPayload = payload
    return payload
  }


  // ── Slides 9 & 10 · the savings goal ──
  //
  // Slide 9 picks a preset, which seeds the name, icon, target and term. Slide
  // 10 lets the user nudge the last two. Nothing here writes to the server: the
  // result rides the payload to OnboardingPersistence, which creates the Goal
  // in the same transaction as the account.

  // Default term, so slide 10 always opens with a stamp lit and a live monthly
  // figure rather than an unanswered row. A preset may override it.


  #budgetNameForTier(tierKey) {
    if (!tierKey) return null
    if (tierKey === "custom") {
      return this.hasManualNameInputTarget ? this.manualNameInputTarget.value.trim() || null : null
    }
    const card = this.tierCardTargets.find(c => c.dataset.tierKey === tierKey)
    return card?.dataset.tierName || null
  }

  // Budget icons are now index-based 3D PNGs (see BudgetsHelper). New budgets
  // default to icon 9 (rocket) for a fresh-start feel; the user can change it
  // from the edit form's icon picker.
  #budgetEmojiForTier(tierKey) {
    return tierKey ? "9" : null
  }

  // Map slide index <-> URL slug. Order matches the partials in show.html.erb.
  // The paywall closes the flow. Auth creates the account but deliberately
  // leaves `onboarded` false; the server sends the user back here at
  // `trial_intro`, and only a completed purchase flips `onboarded` and
  // releases them into the app. A lapsed subscriber re-enters straight at
  // `paywall` (OnboardingController#show picks the entry slide).
  static stepSlugs = [
    "welcome", "problem", "solution", "name",
    "greeting",
    "help_intent",
    "financial_goals",
    "budget_amount", "budget",
    "voice", "widgets",
    "auth", "trial_intro", "trial_reminder", "paywall"
  ]

  // Fallback window, used only until a preset is picked. Each goal carries its
  // own range (GoalsHelper::ONBOARDING_GOAL_PRESETS) because one flat ceiling
  // cannot serve both a $15k holiday and an $80k house deposit. The server
  // validates against the same table.
  static STORAGE_KEY = "floosyo:onboarding"

  // How long the greeting takes to type itself out, start to finish. Paced so
  // the sentence is read rather than glimpsed — the whole point of the slide is
  // that the user notices their own name coming back at them.
  static GREETING_TYPE_MS = 1000

  slugForStep(index) {
    return this.constructor.stepSlugs[index] || String(index)
  }

  // Index of a slide by name. Every "am I on slide N" test in this controller
  // goes through here rather than a literal: the numbers shift every time a
  // step is inserted, and hand-shifting a dozen of them is how the flow and
  // this file drifted apart the last time. Returns -1 for an unknown slug, so
  // a comparison against it is simply never true.
  #at(slug) {
    return this.constructor.stepSlugs.indexOf(slug)
  }

  stepForSlug(slug) {
    if (slug == null) return -1
    const slugs = this.constructor.stepSlugs
    const direct = slugs.indexOf(slug)
    if (direct !== -1) return direct
    const asNum = parseInt(slug, 10)
    if (!Number.isNaN(asNum) && asNum >= 0 && asNum < slugs.length) return asNum
    return -1
  }

  syncUrlForStep(index) {
    const slug = this.slugForStep(index)
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.get("step") === slug) return
      url.searchParams.set("step", slug)
      window.history.replaceState(window.history.state, "", url)
    } catch (_) { /* no-op: URL APIs unavailable */ }
  }

  restoreStepFromUrl() {
    // The server's entry slide wins over both the URL and any cached progress.
    // It is only ever set for a lapsed subscriber, who must land on the paywall
    // and must not be walked back through a questionnaire they already
    // answered — including by a stale `?step=` or localStorage resume.
    const forced = this.stepForSlug(this.startStepValue)
    if (forced > 0) {
      this.highestValue = Math.max(this.highestValue, forced)
      // The server already rendered this slide `active`, so adopt it rather
      // than calling goTo — goTo would animate away from slide 0, flashing the
      // welcome screen on the way in.
      this.currentValue = forced
      this.#forceActiveSlide(forced)
      this.syncUrlForStep(forced)
      this.updateDots()
      this.updateNav()
      this.refreshUserName()
      this.exposeOnboardingPayload()
      return
    }

    let slug = null
    try { slug = new URL(window.location.href).searchParams.get("step") } catch (_) { return }
    const target = this.stepForSlug(slug)
    if (target <= 0) {
      // No step in URL — reset to welcome explicitly. Without this, a Turbo
      // restore that hands back a cached snapshot whose `.slide.active` was
      // on a later step (e.g. auth after sign-out) would leave the user on
      // that stale slide because `currentValue` defaults to 0 while the DOM
      // says otherwise. Reconcile DOM to slide 0 so post-sign-out lands on
      // the welcome screen as expected.
      this.#forceActiveSlide(0)
      this.currentValue = 0
      this.highestValue = 0
      this.syncUrlForStep(0)
      return
    }
    // Allow deep-link past the highest-seen guard (user previously reached this slide on this device).
    this.highestValue = Math.max(this.highestValue, target)
    this.goTo(target, "forward")
  }

  // Hard-reset which slide is `active` in the DOM. Used when reconciling a
  // restored snapshot whose `.slide.active` class doesn't match the
  // controller's authoritative `currentValue`.
  #forceActiveSlide(index) {
    if (!this.hasSlideTarget) return
    this.slideTargets.forEach((s, i) => {
      s.classList.remove("active", "exit-forward", "exit-back")
      if (i === index) s.classList.add("active")
    })
    if (this.hasBgGradientTarget) {
      this.bgGradientTarget.dataset.step = index
    }
  }

  disconnect() {
    this.element.removeEventListener("touchstart", this._onTouchStart)
    this.element.removeEventListener("touchend", this._onTouchEnd)
    this.stopWidgetDeck()
    this.cancelWaysDemo()
    this.cancelGreetingType()
  }

  get arrowForward() { return this.rtlValue ? "\u2190" : "\u2192" }
  get arrowBack() { return this.rtlValue ? "\u2192" : "\u2190" }

  // Navigation

  next() { this.goTo(this.currentValue + 1, "forward") }

  // The greeting is a payoff screen, not a question — there is no answer on it
  // to go back and change. Step over it to the slide that actually holds one.
  back() {
    const target = this.currentValue - 1
    const step = target === this.#at("greeting") ? target - 1 : target
    this.goTo(step, "back")
  }

  goTo(index, direction) {
    if (index < 0 || index >= this.totalValue) return
    if (index > this.highestValue + 1) return

    const dir = direction || (index > this.currentValue ? "forward" : "back")

    this.slideTargets[this.currentValue].classList.remove("active")
    this.slideTargets[this.currentValue].classList.add(dir === "forward" ? "exit-forward" : "exit-back")

    this.currentValue = index
    if (index > this.highestValue) this.highestValue = index

    setTimeout(() => {
      this.slideTargets.forEach(s => s.classList.remove("exit-forward", "exit-back"))
      this.slideTargets[this.currentValue].classList.add("active")
      // Swap the CTA label here, with the slide, rather than on the tap that
      // started this. Written on the tap it changes under the user's finger,
      // which reads as the button flashing into a different button.
      this.applyNextLabel()
    }, 50)

    this.bgGradientTarget.dataset.step = this.currentValue
    this.syncUrlForStep(this.currentValue)
    this.updateDots()
    this.updateNav({ deferLabel: true })
    this.refreshUserName()
    // Refresh window.__onboardingPayload so the auth bridges always see
    // the latest answers when the user reaches the auth step. No
    // localStorage write — onboarding does not persist across reopens.
    this.exposeOnboardingPayload()


    // Recompute dynamic slide content when arriving. Slides are addressed by
    // name (see #at) rather than by number — the running order lives in
    // stepSlugs and in the partials' data-slide, and nowhere else.
    if (this.currentValue === this.#at("voice")) {
      this.runWaysDemo(0)
    } else {
      this.cancelWaysDemo()
    }
    // The deck replays from empty every time it is arrived at, and advances on
    // its own while it is on screen. Off screen it stops dead: a timer running
    // behind twenty other slides is twenty slides' worth of wasted frames.
    if (this.currentValue === this.#at("widgets")) {
      this.startWidgetDeck()
    } else {
      this.stopWidgetDeck()
    }
    // Runs after refreshUserName above, so the name is already in the bubble by
    // the time the sentence is split for typing.
    if (this.currentValue === this.#at("greeting")) {
      this.typeGreeting()
    } else {
      this.cancelGreetingType()
    }
    // The ruler is measured, so it is built on entry rather than at connect:
    // the slide is display:none until then and every width would read as zero.
    if (this.currentValue === this.#at("budget_amount")) {
      this.buildAmountRuler()
      this.paintAmount()
    }
    // Same reason as the budget dial: the track reads zero width until its
    // slide is on screen.
    // Priced off whatever the amount ended up as, every time the picker opens.
    if (this.currentValue === this.#at("budget")) {
      this.repriceFrameworks()
    }
    // The done slide was removed — auth is the closer. renderDone is
    // intentionally not called anymore.

    // Confetti was tied to the now-removed `done` slide. Auth is the
    // closer and shouldn't celebrate before the user has actually signed
    // in. Leave the helper in place; just don't auto-fire it on entry.
  }

  // ── Slide 4 · the greeting types itself ────────────────────────────────
  //
  // The characters are not appended one by one: the bubble is width:fit-content,
  // so growing the text would resize the bubble on every frame and the whole
  // slide would jitter. Instead the sentence is split into one span per
  // character up front — final layout from the first frame — and the spans are
  // then revealed in order.
  //
  // The split walks text nodes rather than parsing the copy, so the <br> and the
  // highlighted <bdi> holding the name survive untouched, and any locale's
  // markup works without knowing its shape.
  //
  // One rAF loop drives it, for the same reason #typeIntoField does: dozens of
  // per-character timers get coalesced and fire in clumps, which reads as
  // stuttering.

  typeGreeting() {
    this.cancelGreetingType()
    if (!this.hasGreetTextTarget) return

    const el = this.greetTextTarget

    // First arm captures the server-rendered sentence; every later arm restores
    // it, so re-entering the slide never splits an already-split sentence.
    if (this.greetTemplate == null) {
      this.greetTemplate = el.innerHTML
    } else {
      el.innerHTML = this.greetTemplate
      // innerHTML wiped the userNameCell node this slide shares with the rest
      // of the flow, so put the typed name back before splitting.
      this.refreshUserName()
    }

    const chars = this.#splitGreetingChars(el)
    if (chars.length === 0) return

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
    if (reduced) {
      chars.forEach(span => span.classList.add("is-typed"))
      return
    }

    const ms = this.constructor.GREETING_TYPE_MS
    const startedAt = performance.now()
    let shown = 0

    const tick = (now) => {
      const t = Math.min(1, (now - startedAt) / ms)
      const target = Math.round(t * chars.length)
      while (shown < target) chars[shown++].classList.add("is-typed")
      if (t < 1) {
        this.greetTypeFrame = requestAnimationFrame(tick)
      } else {
        this.greetTypeFrame = null
      }
    }
    this.greetTypeFrame = requestAnimationFrame(tick)
  }

  cancelGreetingType() {
    if (!this.greetTypeFrame) return
    cancelAnimationFrame(this.greetTypeFrame)
    this.greetTypeFrame = null
  }

  // Replaces every text node under `root` with one span per character, and
  // returns those spans in document order. Whitespace keeps its own span so the
  // reveal advances through gaps at the same rate as through words.
  #splitGreetingChars(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    const textNodes = []
    let node
    while ((node = walker.nextNode())) textNodes.push(node)

    const spans = []
    textNodes.forEach(textNode => {
      const text = textNode.nodeValue
      if (!text) return
      const frag = document.createDocumentFragment()
      Array.from(text).forEach(char => {
        const span = document.createElement("span")
        span.className = "greet-char"
        span.textContent = char
        frag.appendChild(span)
        spans.push(span)
      })
      textNode.parentNode.replaceChild(frag, textNode)
    })
    return spans
  }

  // Step submissions. Indexes are not written down here on purpose: the flow
  // has been cut twice and every hardcoded number went stale. stepSlugs above
  // is the running order, and #at(slug) is the only way to turn a name into an
  // index. Slides that post something are the ones named below.

  async submitAndNext() {
    const step = this.currentValue

    // Info-only slides — just advance. Auth (last) is NOT here: it has its
    // own buttons (Apple/Google/email) and its own bottom-nav handling.
    const infoSteps = new Set([
      "welcome", "problem", "solution",
      "greeting",
      "voice",
      "widgets"
    ].map(name => this.#at(name)))

    // The amount slide. Nothing to gate on — the dial cannot express an illegal
    // value and it is seeded with a legal one — but it is not an info step
    // either: pressing Continue here is what funds the plan.
    //
    // Clearing the skip is the point. "I'd rather not say" withholds the amount
    // and advances by itself (skipBudgetAmount), so the only way to reach this
    // line is the CTA, on a dial showing a real number. Without the reset the
    // skip stayed latched for the rest of the flow: go back to this slide after
    // skipping, press Continue on a dial reading $4,000, and the payload still
    // carried no amount — BudgetFramework.allocate got nil, every framework
    // budget was created open-ended, and the dashboard opened on "No budget
    // set". The flag now means how the user left this slide, not something they
    // once tapped.
    if (step === this.#at("budget_amount")) {
      this.budgetAmountSkipped = false
      this.exposeOnboardingPayload()
      this.next()
      return
    }
    // trial_intro is an explainer with a plain Continue button. Matched by slug
    // rather than index so it survives any future reordering of the flow.
    const slug = this.slugForStep(step)
    if (slug === "trial_intro" || slug === "trial_reminder") {
      this.next()
      return
    }
    if (infoSteps.has(step)) {
      this.next()
      return
    }

    if (step === this.#at("name")) {
      const name = this.nameInputTarget.value.trim()
      if (!name) { this.shakeInput(this.nameInputTarget); return }
      this.next()
    } else if (step === this.#at("financial_goals")) {
      if (!this.selectedGoal) {
        this.shakeChips(this.goalChipTargets)
        return
      }
      this.next()
    } else if (step === this.#at("help_intent")) {
      // Multi-select: at least one intent is required, same shake-to-gate the
      // single-select pickers use.
      if (this.selectedHelpIntents.size === 0) {
        this.shakeChips(this.helpIntentChipTargets)
        return
      }
      this.next()
    } else if (step === this.#at("budget")) {
      this.submitBudgetOptimistic()
    }
  }

  // ── Slide 14 · the monthly amount ──────────────────────────────────────
  //
  // The control is drawn rather than an <input type="range"> so its handle can
  // be the same object as the assign screen's vial grip. Pointer maths is the
  // same fraction-of-the-track the tool pages use.

  #amountBounds() {
    const c = this.constructor
    return { min: c.AMOUNT_MIN, max: c.AMOUNT_MAX, span: c.AMOUNT_MAX - c.AMOUNT_MIN }
  }

  #parseAmount(value) {
    return parseInt(String(value).replace(/[^\d]/g, ""), 10) || 0
  }

  #formatAmount(value) {
    try {
      return value.toLocaleString(document.documentElement.lang || "en")
    } catch (_) {
      return String(value)
    }
  }

  // Rounds to `step`, then holds the result inside the window. Every path into
  // the amount goes through here, so nothing downstream has to re-check it.
  #clampAmount(value, step) {
    const { min, max } = this.#amountBounds()
    const rounded = Math.round(value / step) * step
    return Math.max(min, Math.min(max, rounded))
  }

  setBudgetAmount(value, { step = this.constructor.AMOUNT_STEP, silent = false } = {}) {
    // Touching the control is the user changing their mind about skipping.
    this.budgetAmountSkipped = false
    this.budgetAmount = this.#clampAmount(value, step)
    if (!silent && this.hasAmountInputTarget) {
      this.amountInputTarget.value = this.#formatAmount(this.budgetAmount)
    }
    this.paintAmount()
    this.repriceFrameworks()
    this.exposeOnboardingPayload()
  }

  paintAmount() {
    if (!this.hasAmountTrackTarget) return
    const { min, span } = this.#amountBounds()
    const pct = ((this.budgetAmount - min) / span) * 100

    this.amountFillTarget.style.width = `${pct}%`
    this.amountGlowTarget.style.width = `${pct}%`
    this.amountThumbTarget.style.insetInlineStart = `${pct}%`

    const bubble = this.amountBubbleTarget
    bubble.textContent = this.#formatAmount(this.budgetAmount)
    bubble.style.insetInlineStart = `${pct}%`
    // Centred on the handle, except near the ends where centring would push it
    // off the track. The nudge pulls it back just far enough to stay inside.
    const trackWidth = this.amountTrackTarget.clientWidth
    const half = bubble.offsetWidth / 2
    const x = trackWidth * (pct / 100)
    const nudge = Math.max(0, half - x) - Math.max(0, (x + half) - trackWidth)
    bubble.style.setProperty("--amount-nudge", `${nudge}px`)

    this.amountTicksTarget.querySelectorAll("[data-at]").forEach(tick => {
      tick.classList.toggle("on", Number(tick.dataset.at) <= this.budgetAmount)
    })

    if (this.hasAmountInputTarget) {
      // Sized to its digits so the currency stays against the number instead of
      // floating a fixed width away from a three-digit one.
      const chars = this.amountInputTarget.value.length
      this.amountInputTarget.style.width = `${Math.max(3, chars)}ch`
    }
  }

  // One tick per 500, majors on the labelled stops, each placed at its true
  // fraction so a tick and its label agree with where the handle stops.
  buildAmountRuler() {
    if (!this.hasAmountTicksTarget) return
    const { min, max, span } = this.#amountBounds()
    const labels = [min, 3000, 6000, 9000, max]
    const at = value => ((value - min) / span) * 100

    const ticks = []
    for (let value = min; value <= max; value += 500) {
      const major = labels.includes(value) ? " major" : ""
      ticks.push(`<span class="amount-tick${major}" data-at="${value}" style="inset-inline-start:${at(value)}%"></span>`)
    }
    this.amountTicksTarget.innerHTML = ticks.join("")

    const short = value => (value >= 1000 ? `${value / 1000}k` : String(value))
    this.amountScaleTarget.innerHTML = labels
      .map(value => `<span style="inset-inline-start:${at(value)}%">${short(value)}</span>`)
      .join("")
  }

  selectAmount(event) {
    event.target.select()
  }

  // "I'd rather not say" — advance without an amount. The picker drops back to
  // percentages only and the budgets are created open-ended.
  skipBudgetAmount() {
    this.budgetAmountSkipped = true
    this.repriceFrameworks()
    this.exposeOnboardingPayload()
    this.next()
  }

  // Typing is left alone until blur — clamping mid-keystroke turns "5" into
  // "500" under the cursor. The ceiling is not: a field showing a number the
  // rest of the flow is not using is worse than an interrupted keystroke.
  amountTyped(event) {
    const typed = this.#parseAmount(event.target.value)
    const { max } = this.#amountBounds()
    if (typed > max) {
      event.target.value = this.#formatAmount(max)
      this.flashAmountLimit(event.target)
    }
    this.budgetAmountSkipped = false
    this.budgetAmount = Math.min(max, typed)
    this.paintAmount()
    this.repriceFrameworks()
    this.exposeOnboardingPayload()
  }

  amountSettled() {
    this.setBudgetAmount(this.budgetAmount)
  }

  // A silent clamp reads as a broken keyboard. One pulse says "that is the
  // ceiling" without putting a dialog in the way.
  flashAmountLimit(el) {
    el.classList.remove("at-limit")
    void el.offsetWidth
    el.classList.add("at-limit")
  }

  amountDragStart(event) {
    if (event.target.closest(".amount-input")) return
    event.preventDefault()

    const track = this.amountTrackTarget
    const rect = track.getBoundingClientRect()
    const rtl = this.rtlValue || getComputedStyle(track).direction === "rtl"
    const { min, span } = this.#amountBounds()
    const slider = this.amountSliderTarget

    slider.classList.add("grabbing")
    try { track.setPointerCapture(event.pointerId) } catch (_) { /* no-op */ }

    const move = (ev) => {
      let fraction = (ev.clientX - rect.left) / rect.width
      if (rtl) fraction = 1 - fraction
      fraction = Math.max(0, Math.min(1, fraction))
      this.setBudgetAmount(min + fraction * span, { step: this.constructor.AMOUNT_DETENT })
    }
    const stop = () => {
      slider.classList.remove("grabbing")
      track.removeEventListener("pointermove", move)
      track.removeEventListener("pointerup", stop)
      track.removeEventListener("pointercancel", stop)
    }

    track.addEventListener("pointermove", move)
    track.addEventListener("pointerup", stop)
    track.addEventListener("pointercancel", stop)
    move(event)
  }

  // Whole currency units only: each slot floors to its share and the rounding
  // remainder is handed out one unit at a time, biggest share first, so the
  // parts always add back up to the total. Mirrors BudgetFramework.allocate,
  // which is what actually creates the budgets at sign-in — this is only the
  // preview, and the two must never disagree.
  allocateAmount(total, percentages) {
    if (!(total > 0)) return percentages.map(() => 0)

    const parts = percentages.map(pct => Math.floor((total * pct) / 100))
    let remainder = total - parts.reduce((sum, part) => sum + part, 0)
    if (remainder > 0) {
      const order = percentages
        .map((pct, i) => [pct, i])
        .sort((a, b) => b[0] - a[0] || a[1] - b[1])
        .map(pair => pair[1])
      let n = 0
      while (remainder > 0) { parts[order[n % order.length]] += 1; remainder -= 1; n += 1 }
    }
    return parts
  }

  // Reprices every framework card from the current amount. Cards are grouped by
  // their owning .tier so each framework's remainder lands inside its own split
  // rather than being pooled across the whole slide.
  repriceFrameworks() {
    if (!this.hasFrameworkAmountTarget) return

    // Skipped: the legend falls back to names and shares only. Flagged on the
    // container rather than blanked per node, so the layout collapses to the
    // pre-amount design instead of leaving empty cells behind.
    const legends = this.frameworkAmountTargets
      .map(node => node.closest(".fw-names"))
      .filter((el, i, all) => el && all.indexOf(el) === i)
    legends.forEach(el => el.classList.toggle("is-unpriced", this.budgetAmountSkipped))
    if (this.budgetAmountSkipped) return

    const code    = this.hasCurrencySelectTarget ? this.currencySelectTarget.value : null
    const display = this.currencyMapValue?.[code] || code || ""

    const byCard = new Map()
    this.frameworkAmountTargets.forEach(node => {
      const card = node.closest("[data-rule-id]")
      if (!card) return
      if (!byCard.has(card)) byCard.set(card, [])
      byCard.get(card).push(node)
    })

    byCard.forEach(nodes => {
      const ordered = nodes.sort((a, b) => Number(a.dataset.slotIndex) - Number(b.dataset.slotIndex))
      const parts = this.allocateAmount(this.budgetAmount, ordered.map(n => Number(n.dataset.slotPct)))
      ordered.forEach((node, i) => { node.textContent = this.#fmtMoney(parts[i], display) })
    })
  }

  // Sync every slide's userNameCell target to the current name input value.
  refreshUserName() {
    if (!this.hasUserNameCellTarget || !this.hasNameInputTarget) return
    const live = this.nameInputTarget.value.trim().split(/\s+/)[0]
    if (!live) return
    this.userNameCellTargets.forEach(el => { el.textContent = live })
  }

  // ── Slide 7 · Peace-of-mind question · single-select ──
  selectGoal(event) {
    const btn = event.currentTarget
    this.selectedGoal = btn.dataset.goalKey
    this.goalChipTargets.forEach(el => el.classList.toggle("on", el === btn))
  }

  // Clear the bottom fade mask on the financial-goals chip list once the user has
  // scrolled to the end — the fade is a "more below" hint, so it's noise
  // after they've already seen everything.
  updateGoalScrollFade(event) {
    const el = event.currentTarget
    const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 2
    el.classList.toggle("is-scroll-end", atEnd)
  }

  async submitGoal() {
    if (!this.selectedGoal) return
    const result = await this.postStep("money_goal", { money_goal: this.selectedGoal })
    if (result?.success) this.next()
  }

  // ── Slide 14 · What do you want FloosYo for · MULTI-select ──
  // Toggles rather than replaces. `.on` is the same solid-blue selected state
  // the single-select chips use, so nothing new is needed visually; aria-pressed
  // carries the multi-select semantics that a radio-style chip wouldn't.
  selectHelpIntent(event) {
    const btn = event.currentTarget
    const key = btn.dataset.helpIntentKey
    if (!key) return

    if (this.selectedHelpIntents.has(key)) {
      this.selectedHelpIntents.delete(key)
    } else {
      this.selectedHelpIntents.add(key)
    }

    const on = this.selectedHelpIntents.has(key)
    btn.classList.toggle("on", on)
    btn.setAttribute("aria-pressed", String(on))
    this.exposeOnboardingPayload()
  }

  // Locale-aware money formatter — mirrors the server-side money_prefix? helper.
  // Prefixes the symbol ("$1,234") when the locale is non-RTL and the currency
  // is a single-glyph symbol; otherwise suffixes ("1,234 د.أ").
  #fmtMoney(amount, currencyDisplay) {
    const locale = this.rtlValue ? "ar-EG" : "en-US"
    const num    = Math.round(amount || 0).toLocaleString(locale)
    const sym    = currencyDisplay || ""
    const prefix = !this.rtlValue && sym.length === 1
    return prefix ? `${sym}${num}` : `${num} ${sym}`
  }

  // Name-only submit. Currency + timezone are handled on a dedicated later screen.
  async submitName() {
    const name = this.nameInputTarget.value.trim()
    if (!name) { this.shakeInput(this.nameInputTarget); return false }

    const result = await this.postStep("name", { name })
    return !!result?.success
  }

  // ── Slide 22 · Home Screen widget deck ──
  //
  // Four cards, one on screen at a time: the three medium widgets, then the two
  // smalls side by side in the room one medium takes. Arrows and dots move the
  // deck, and it moves itself on a hold so the whole set gets seen without a
  // tap.
  //
  // Every arriving card replays from empty on ONE clock — the mascots growing
  // in, the ring sweeps, the goal's water level, its bubble field, the counting
  // money and the counting percent all read the same eased t. That is the web
  // read of WidgetKit handing back an empty timeline entry followed by a full
  // one, which is exactly what the installed widget does the moment it lands on
  // the Home Screen. Sharing the clock is what makes it land as one movement
  // instead of five things that happen to start together.
  nextWidget() { this.#moveWidget(1) }

  previousWidget() { this.#moveWidget(-1) }

  selectWidget(event) {
    const index = Number(event.currentTarget.dataset.widgetIndex)
    if (!Number.isNaN(index)) this.#showWidget(index)
  }

  startWidgetDeck() {
    if (!this.hasWidgetCardTarget) return

    this.widgetDeckOn = true

    // The fill IS the entrance, so it waits for the slide to be on screen.
    // goTo hands the slide its `active` class a beat after the tap; started on
    // the tap instead, the clock would be a quarter done behind the cross-fade
    // and the card would arrive already part full. On a deep link the slide is
    // active from the start and there is nothing to wait for.
    clearTimeout(this.widgetOpenTimer)
    const slide = this.widgetCardTargets[0].closest(".slide")
    if (slide && !slide.classList.contains("active")) {
      this.widgetOpenTimer = setTimeout(() => this.#showWidget(this.widgetAt || 0), 60)
      return
    }

    this.#showWidget(this.widgetAt || 0)
  }

  stopWidgetDeck() {
    this.widgetDeckOn = false
    clearTimeout(this.widgetOpenTimer)
    if (this.widgetTimer) { clearInterval(this.widgetTimer); this.widgetTimer = null }
    if (this.widgetRaf) { cancelAnimationFrame(this.widgetRaf); this.widgetRaf = null }
  }

  #moveWidget(step) {
    const total = this.widgetCardTargets.length
    if (!total) return

    this.#showWidget(((this.widgetAt || 0) + step + total) % total)
  }

  #showWidget(index) {
    this.widgetAt = index

    let arriving = null
    this.widgetCardTargets.forEach(card => {
      const on = Number(card.dataset.widgetIndex) === index
      card.classList.toggle("is-current", on)
      if (on) arriving = card
    })
    this.widgetDotTargets.forEach(dot => {
      dot.classList.toggle("is-on", Number(dot.dataset.widgetIndex) === index)
    })

    const copy = this.#widgetCopyData()[index]
    if (copy) {
      this.widgetNameTarget.textContent = copy.name
      this.widgetSizeTarget.textContent = copy.size
      this.widgetLineTarget.textContent = copy.line
    }

    this.#playWidget(arriving)
    // A tap is deliberate, so the hold restarts from there rather than yanking
    // the card the user just asked for away a moment later.
    this.#armWidgetTimer()
  }

  #armWidgetTimer() {
    if (this.widgetTimer) { clearInterval(this.widgetTimer); this.widgetTimer = null }
    if (!this.widgetDeckOn) return

    this.widgetTimer = setInterval(() => this.#moveWidget(1), this.constructor.WIDGET_HOLD_MS)
  }

  // Replays one card from empty. Mascots and rings ride the compositor (a
  // keyframe and a stroke transition, both on the same curve as the clock); the
  // water, the bubbles and the figures are re-traced per frame, because their
  // shape changes rather than their transform.
  #playWidget(card) {
    if (!card) return
    if (this.widgetRaf) { cancelAnimationFrame(this.widgetRaf); this.widgetRaf = null }

    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true
    const duration = still ? 0 : this.constructor.WIDGET_FILL_MS

    card.querySelectorAll(".ow-mascot").forEach((mascot, i) => {
      mascot.classList.remove("is-growing")
      void mascot.offsetWidth               // restart the keyframe on a re-show
      mascot.style.animationDelay = `${60 + i * 70}ms`
      mascot.classList.add("is-growing")
    })

    card.querySelectorAll(".ow-ring-arc").forEach(arc => {
      arc.style.transition = "none"
      arc.style.strokeDashoffset = arc.getAttribute("stroke-dasharray")
      void arc.getBoundingClientRect()      // commit the empty ring before easing off it
      arc.style.transition = still ? "none" : `stroke-dashoffset ${duration}ms cubic-bezier(.22, 1, .36, 1)`
      arc.style.strokeDashoffset = arc.dataset.owDash
    })

    const cels = {
      water: Array.from(card.querySelectorAll("[data-ow-water]")),
      bubbles: Array.from(card.querySelectorAll("[data-ow-bubbles]")),
      money: Array.from(card.querySelectorAll("[data-ow-money]")),
      percent: Array.from(card.querySelectorAll("[data-ow-pct]"))
    }
    if (!cels.water.length && !cels.bubbles.length && !cels.money.length && !cels.percent.length) return

    const started = performance.now()
    const frame = (now) => {
      const t = duration ? Math.min((now - started) / duration, 1) : 1
      this.#paintWidgetFill(cels, 1 - Math.pow(1 - t, 3))
      this.widgetRaf = t < 1 ? requestAnimationFrame(frame) : null
    }
    // The first frame runs inline, not on the next tick. The markup carries the
    // resting values so the card reads correctly with JavaScript off, and
    // waiting a frame to zero them would flash the finished figure before it
    // starts counting.
    frame(started)
  }

  #paintWidgetFill(cels, eased) {
    cels.water.forEach(host => {
      const w = Number(host.dataset.owW)
      const h = Number(host.dataset.owH)
      const level = Number(host.dataset.owWater) * eased

      // The meniscus never sits flush against the leading edge: at a level of
      // zero its round cap would read as a bright seam down the card.
      const crest = this.#waterPoints(w, h, Math.max(level, 0.02))
      const surface = crest.map((p, i) => `${i ? "L" : "M"}${p}`).join(" ")
      host.querySelectorAll(".ow-wave-line").forEach(line => line.setAttribute("d", surface))

      const body = host.querySelector(".ow-water-body")
      if (!body) return
      // Below the clamp the body and the crest part company, so the water can
      // still empty completely while the meniscus stays a hair off the edge.
      const edge = level >= 0.02 ? crest : this.#waterPoints(w, h, level)
      body.setAttribute("d", `M0,0 L${edge.join(" L")} L0,${h} Z`)
    })

    cels.bubbles.forEach(field => {
      field.style.width = `${Number(field.dataset.owBubbles) * eased}px`
      field.style.opacity = eased > 0.05 ? "1" : "0"
    })

    cels.money.forEach(el => this.#paintWidgetMoney(el, Number(el.dataset.owMoney) * eased))
    cels.percent.forEach(el => { el.textContent = `${Math.floor(Number(el.dataset.owPct) * eased)}%` })
  }

  // FYWaterShape. A vertical sine of amplitude 4.2 over a 64pt period, sampled
  // in 48 steps down the canvas; the crest is a fixed silhouette and the level
  // slides it across, which is why the wave never appears to scroll.
  #waterPoints(w, h, progress) {
    const amp = this.constructor.WATER_AMP
    const wavelength = this.constructor.WATER_WAVELENGTH
    const steps = this.constructor.WATER_STEPS

    const span = Math.max(w - amp * 2, 0)
    const mean = amp + span * Math.min(Math.max(progress, 0), 1)

    const points = new Array(steps + 1)
    for (let step = 0; step <= steps; step++) {
      const y = h * (step / steps)
      points[step] = `${(mean + amp * Math.sin((2 * Math.PI * y) / wavelength)).toFixed(2)},${y.toFixed(2)}`
    }
    return points
  }

  // Re-renders a hero figure in place. The whole units and the fraction sit in
  // their own elements, so the currency symbol and the 60% fraction sizing the
  // partial rendered stay exactly where they are and only the digits move.
  // Cents are rounded, never floored: 1673.65 * 100 is 167364.99999999997 in
  // binary floating point, and flooring that lands the count on .64.
  #paintWidgetMoney(el, value) {
    const cents = Math.round(value * 100)
    const compact = el.dataset.owMode === "compact"

    const whole = el.querySelector(".ow-money-whole")
    if (whole) {
      const units = compact ? Math.round(value) : Math.floor(cents / 100)
      whole.textContent = this.#groupDigits(units, el.dataset.owDelim || ",")
    }

    const fraction = el.querySelector(".ow-money-frac")
    if (fraction) fraction.textContent = `.${String(cents % 100).padStart(2, "0")}`
  }

  // number_with_delimiter's grouping, with the locale's own delimiter, so the
  // last frame lands on exactly the string the partial rendered.
  #groupDigits(value, delimiter) {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, delimiter)
  }

  // Names, sizes and lines for every card, rendered once into a hidden element
  // so paging never needs a round trip.
  #widgetCopyData() {
    if (this.widgetCopyCache) return this.widgetCopyCache

    try {
      this.widgetCopyCache = JSON.parse(this.widgetCopyTarget.dataset.widgetCopy)
    } catch (_error) {
      this.widgetCopyCache = []
    }

    return this.widgetCopyCache
  }

  // ── Slide 13 · Three ways in · Scan, Speak, Type, each turned into an expense ──
  //
  // The stage is the dashboard's add-expense sheet, replayed: expense-deck owns
  // the tabs, the thumb and the deck exactly as it does on the dashboard; this
  // code performs the user's part (press the tile, tap the mascot, type) and
  // then the sheet's (the analyzing skeleton, the confirm card), swapped into
  // the one slot under the deck the way the sheet swaps its own input area. It
  // loops Scan → Speak → Type until the slide is left.
  //
  // Any tap restarts the loop at that way in: a deck swipe arrives as
  // expense-deck:tabchange (selectWay); the dock tabs, the panels and the photo
  // tiles carry a data-way (replayWay). Tab moves made by the loop itself are
  // flagged so they do not restart it.
  //
  // Continue is deliberately never locked here: "voice" sits in the infoSteps
  // set of submitAndNext, so the user may leave at any point of the demo.
  //
  // Typing is driven by one rAF loop rather than one setTimeout per character.
  // Dozens of short timers get coalesced and fire in clumps, which is what made
  // the old typewriter look choppy; a single frame-synced loop stays smooth
  // however long the transcript is.
  static WAYS = ["snap", "speak", "type"]
  static WAYS_RECORDING_SECS = 15   // RECORDING_DURATION_SECS, voice_modal_controller.js

  runWaysDemo(startAt = 0) {
    if (!this.hasWaysDemoTarget) return
    this.cancelWaysDemo()
    const run = {}
    this.waysRun = run
    this.waysTimers = []

    const loop = async () => {
      let i = startAt
      while (this.waysRun === run) {
        const way = this.constructor.WAYS[i]
        this.#selectWayTab(i)
        this.#resetWaysSlot(i)
        await this.#waysSleep(700)
        if (this.waysRun !== run) return
        if (way === "snap")       await this.#playSnap(run)
        else if (way === "speak") await this.#playSpeak(run)
        else                      await this.#playType(run)
        if (this.waysRun !== run) return
        await this.#convertWay(way, run)
        i = (i + 1) % this.constructor.WAYS.length
      }
    }
    loop()
  }

  cancelWaysDemo() {
    this.waysRun = null
    this.waysSelecting = false
    if (this.waysSettleFrame) {
      cancelAnimationFrame(this.waysSettleFrame)
      this.waysSettleFrame = null
    }
    if (this.waysTimers) {
      this.waysTimers.forEach(id => clearTimeout(id))
      this.waysTimers = []
    }
    if (this.waysTypeFrame) {
      cancelAnimationFrame(this.waysTypeFrame)
      this.waysTypeFrame = null
    }
    if (!this.hasWaysDemoTarget) return
    this.#resetWaysSlot(this.waysCurrent ?? 0)
  }

  // A deck swipe (dock taps come in through replayWay). The loop's own tab
  // moves are ignored, and so is the whole of the smooth scroll they start:
  // expense-deck stops reading scroll events 550ms after it scrolls, but the
  // scroll itself can outlast that, and while it is still passing a panel the
  // deck rounds to that panel and reports it — a change of mind the user
  // never had. waysSelecting stays up until the deck has actually arrived.
  selectWay(event) {
    // expense-deck also reports its first apply, on connect, which is page load
    // on the welcome slide: nothing to replay yet.
    if (this.currentValue !== this.#at("voice")) return
    if (this.waysSelecting) return
    const index = Number(event.detail?.index)
    if (Number.isNaN(index)) return
    if (index === this.waysCurrent) return
    this.runWaysDemo(index)
  }

  // A tap on a dock tab, a panel or a photo tile. Always restarts, even on the
  // way in already playing: a tap should visibly do something.
  replayWay(event) {
    const index = this.constructor.WAYS.indexOf(event.currentTarget.dataset.way)
    if (index < 0) return
    this.runWaysDemo(index)
  }

  #selectWayTab(index) {
    this.waysCurrent = index

    // The thumb's place, by formula rather than by measurement: every tab is
    // 76px and the bar pads 8px (the dock's own w-[76px] and px-2). On iOS
    // WebKit the measured translate expense-deck sets was seen left on Scan
    // with Speak or Type active; onboarding.css pins the thumb to this
    // variable instead, mirrored by inset-inline-start in RTL.
    const dock = this.waysDemoTarget.querySelector("[data-expense-deck-target='dock']")
    if (dock) dock.style.setProperty("--ways-thumb-x", `${8 + 76 * index}px`)

    const deck = this.application.getControllerForElementAndIdentifier(this.waysDemoTarget, "expense-deck")
    if (!deck) return
    this.waysSelecting = true
    deck.selectIndex(index)

    // Hold the flag until the deck has physically landed on the panel (or a
    // generous ceiling, for a deck that never scrolls, e.g. reduced motion).
    const el = this.waysDemoTarget.querySelector("[data-expense-deck-target='deck']")
    const width = el ? el.clientWidth : 0
    const startedAt = performance.now()
    const settle = () => {
      if (this.waysCurrent !== index) return            // superseded by a newer move
      const at = el ? Math.abs(el.scrollLeft) : 0
      const arrived = !width || Math.abs(at - width * index) <= 2
      if (arrived || performance.now() - startedAt > 1500) {
        this.waysSelecting = false
        // Apply once more now that layout has settled: the thumb is placed
        // off measured rects, and a rect read mid-scroll on WebKit has been
        // seen to leave it on the previous tab. Same index, so the tabchange
        // this raises is ignored by selectWay.
        deck.selectIndex(index)
        return
      }
      this.waysSettleFrame = requestAnimationFrame(settle)
    }
    this.waysSettleFrame = requestAnimationFrame(settle)
  }

  #waysSleep(ms) {
    return new Promise(resolve => {
      const id = setTimeout(resolve, ms)
      if (this.waysTimers) this.waysTimers.push(id)
    })
  }

  // The slot back to its input for the way in showing: the tiles on Scan, an
  // empty field otherwise, skeleton and cards away, mascot at rest.
  #resetWaysSlot(index) {
    const onScan = index === 0
    if (this.hasWaysFieldTarget)    this.waysFieldTarget.classList.toggle("hidden", onScan)
    if (this.hasWaysPhotoRowTarget) this.waysPhotoRowTarget.classList.toggle("hidden", !onScan)
    if (this.hasWaysLoadingTarget)  this.waysLoadingTarget.classList.add("hidden")
    this.waysCardTargets.forEach(card => card.classList.add("hidden"))

    if (this.hasWaysTypedTarget)       this.waysTypedTarget.textContent = ""
    if (this.hasWaysCaretTarget)       this.waysCaretTarget.classList.add("hidden")
    if (this.hasWaysPlaceholderTarget) this.waysPlaceholderTarget.classList.remove("hidden")
    if (this.hasWaysCountTarget)       this.waysCountTarget.textContent = "0 / 200"
    if (this.hasWaysFieldBoxTarget)    this.waysFieldBoxTarget.classList.remove("is-live")
    if (this.hasWaysTakePhotoTarget)   this.waysTakePhotoTarget.classList.remove("is-pressed")

    this.waysDemoTarget.classList.remove("is-done")
    this.#stopListening()
  }

  #stopListening() {
    if (this.waysTimerTick) {
      clearInterval(this.waysTimerTick)
      this.waysTimerTick = null
    }
    if (this.hasWaysRingsTarget)     this.waysRingsTarget.classList.add("hidden")
    if (this.hasWaysMascotOnTarget)  this.waysMascotOnTarget.classList.add("hidden")
    if (this.hasWaysMascotOffTarget) {
      this.waysMascotOffTarget.classList.remove("hidden", "is-tapped")
    }
    if (this.hasWaysStatusTarget) this.waysStatusTarget.textContent = this.waysStatusTarget.dataset.idle || ""
    if (this.hasWaysTimerTarget) {
      this.waysTimerTarget.classList.add("hidden")
      this.waysTimerTarget.textContent = ""
    }
  }

  // Types `text` into the field over `ms`, then holds the caret for a beat.
  async #typeIntoField(text, ms, run) {
    if (this.hasWaysPlaceholderTarget) this.waysPlaceholderTarget.classList.add("hidden")
    if (this.hasWaysCaretTarget)       this.waysCaretTarget.classList.remove("hidden")
    if (this.hasWaysFieldBoxTarget)    this.waysFieldBoxTarget.classList.add("is-live")

    const chars = Array.from(text)
    await new Promise(resolve => {
      const startedAt = performance.now()
      const tick = (now) => {
        if (this.waysRun !== run) { resolve(); return }
        const t = Math.min(1, (now - startedAt) / ms)
        const shown = Math.round(t * chars.length)
        if (this.hasWaysTypedTarget) this.waysTypedTarget.textContent = chars.slice(0, shown).join("")
        if (this.hasWaysCountTarget) this.waysCountTarget.textContent = `${shown} / 200`
        if (t < 1) this.waysTypeFrame = requestAnimationFrame(tick)
        else resolve()
      }
      this.waysTypeFrame = requestAnimationFrame(tick)
    })
    await this.#waysSleep(350)
    if (this.waysRun !== run) return
    if (this.hasWaysCaretTarget) this.waysCaretTarget.classList.add("hidden")
  }

  // Scan: the Take photo tile is pressed. The sheet shows nothing between the
  // camera and the skeleton, so neither does the demo.
  async #playSnap(run) {
    await this.#waysSleep(500)
    if (this.waysRun !== run) return
    const tile = this.hasWaysTakePhotoTarget ? this.waysTakePhotoTarget : null
    if (tile) tile.classList.add("is-pressed")
    await this.#waysSleep(220)
    if (tile) tile.classList.remove("is-pressed")
    if (this.waysRun !== run) return
    await this.#waysSleep(300)
  }

  // Speak: the mascot is tapped, the rings pulse, the timer runs, the words
  // land in the field, then the mascot rests again.
  async #playSpeak(run) {
    await this.#waysSleep(400)
    if (this.waysRun !== run) return
    if (this.hasWaysMascotOffTarget) this.waysMascotOffTarget.classList.add("is-tapped")
    await this.#waysSleep(160)
    if (this.hasWaysMascotOffTarget) this.waysMascotOffTarget.classList.remove("is-tapped")
    if (this.waysRun !== run) return

    if (this.hasWaysMascotOffTarget) this.waysMascotOffTarget.classList.add("hidden")
    if (this.hasWaysMascotOnTarget)  this.waysMascotOnTarget.classList.remove("hidden")
    if (this.hasWaysRingsTarget)     this.waysRingsTarget.classList.remove("hidden")
    if (this.hasWaysStatusTarget)    this.waysStatusTarget.textContent = this.waysStatusTarget.dataset.listening || ""
    // The sheet's own countdown: RECORDING_DURATION_SECS in
    // voice_modal_controller.js, shown with the locale's second unit.
    let remaining = this.constructor.WAYS_RECORDING_SECS
    if (this.hasWaysTimerTarget) {
      this.waysTimerTarget.textContent = `${remaining}${timerUnit()}`
      this.waysTimerTarget.classList.remove("hidden")
    }
    this.waysTimerTick = setInterval(() => {
      remaining = Math.max(0, remaining - 1)
      if (this.hasWaysTimerTarget) this.waysTimerTarget.textContent = `${remaining}${timerUnit()}`
    }, 1000)

    const text = this.hasWaysStringsTarget ? (this.waysStringsTarget.dataset.speak || "") : ""
    await this.#typeIntoField(text, 1900, run)
    if (this.waysRun !== run) return
    this.#stopListening()
    await this.#waysSleep(450)
  }

  // Type: the words are typed in, plain digits and all.
  async #playType(run) {
    await this.#waysSleep(400)
    if (this.waysRun !== run) return
    const text = this.hasWaysStringsTarget ? (this.waysStringsTarget.dataset.type || "") : ""
    await this.#typeIntoField(text, 1300, run)
    await this.#waysSleep(350)
  }

  // What the sheet does with it, below the wallpaper: the analyzing skeleton
  // appears, then the confirm card lands, and the mascot is pleased with it.
  // The input stays as it is above, so what went in and what came out are on
  // screen together.
  async #convertWay(way, run) {
    if (this.hasWaysAnalyzingTarget) {
      this.waysAnalyzingTarget.textContent = this.waysAnalyzingTarget.dataset[way] || ""
    }
    if (this.hasWaysLoadingTarget) this.waysLoadingTarget.classList.remove("hidden")
    await this.#waysSleep(1300)
    if (this.waysRun !== run) return
    if (this.hasWaysLoadingTarget) this.waysLoadingTarget.classList.add("hidden")

    const card = this.waysCardTargets.find(el => el.dataset.way === way)
    if (card) {
      card.classList.remove("hidden")
      // Re-arm the entrance keyframe on a card that has played before.
      const inner = card.querySelector(".voice-card-in")
      if (inner) {
        inner.style.animation = "none"
        void inner.offsetWidth
        inner.style.animation = ""
      }
    }
    this.waysDemoTarget.classList.add("is-done")
    await this.#waysSleep(2600)
  }

  async submitSettings() {
    const currency = this.currencySelectTarget.value
    const timezone = this.timezoneSelectTarget.value

    const result = await this.postStep("settings", { currency, timezone })
    if (result.success) {
      const display = this.currencyMapValue[currency] || currency
      this.currencyLabelTargets.forEach(el => {
        el.textContent = display
        el.dataset.currencyCode = currency
      })
      this.refreshTierAmounts(currency)
      this.next()
    }
  }

  // Budget slide: the user picks a budgeting framework (50/30/20, etc.). The
  // named, open-ended budgets are created server-side by OnboardingPersistence;
  // the user funds them next on the existing /budgets/assign screen.
  submitBudgetOptimistic() {
    if (!this.selectedRule) {
      const first = this.ruleCardTargets[0]
      if (first) {
        first.classList.add("shake")
        setTimeout(() => first.classList.remove("shake"), 600)
      }
      return
    }
    this.budgetTierKey    = "custom"
    this.budgetLimit      = null
    this.budgetCategories = []
    this.next()
  }

  selectBudgetRule(event) {
    const card = event.currentTarget
    this.ruleCardTargets.forEach(c => c.classList.remove("selected"))
    card.classList.add("selected")
    this.selectedRule = card.dataset.ruleId
    this.exposeOnboardingPayload()
  }

  // Tier selection (legacy — retained for currency refresh; no cards rendered)

  selectTier(event) {
    if (event.target.closest(".adjust-btn")) return

    const card = event.currentTarget
    const wasSelected = card.classList.contains("selected")

    this.tierCardTargets.forEach(t => t.classList.remove("selected"))

    if (wasSelected) {
      this.selectedTierKey = null
    } else {
      card.classList.add("selected")
      this.selectedTierKey = card.dataset.tierKey

      if (this.hasManualFormTarget) {
        this.manualFormTarget.classList.remove("open")
        this.manualToggleTarget.classList.remove("open")
      }
      this.manualMode = false
    }
  }

  adjustCategory(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const delta = parseInt(btn.dataset.delta)
    const row = btn.closest(".cat-right")
    const amountEl = row.querySelector(".cat-amount")
    const currentVal = parseInt(amountEl.textContent) || 0
    const newVal = Math.max(0, currentVal + delta)
    amountEl.textContent = newVal

    // Recalculate tier total
    const tier = btn.closest(".tier")
    let total = 0
    tier.querySelectorAll(".cat-amount").forEach(a => {
      total += parseInt(a.textContent) || 0
    })
    const tierTotalEl = tier.querySelector("[data-onboarding-target='tierTotal']")
    if (tierTotalEl) tierTotalEl.textContent = total
    const btValNum = tier.querySelector(".bt-val-num")
    if (btValNum) btValNum.textContent = total
  }

  toggleManual() {
    if (!this.hasManualFormTarget) return

    const isOpening = !this.manualFormTarget.classList.contains("open")
    this.manualFormTarget.classList.toggle("open")
    this.manualToggleTarget.classList.toggle("open")

    if (isOpening) {
      this.tierCardTargets.forEach(t => t.classList.remove("selected"))
      this.selectedTierKey = null
      this.manualMode = true
    } else {
      this.manualMode = false
    }
  }

  // Reminder / notification step

  async enableNotifications() {
    const btn = this.reminderEnableBtnTarget
    btn.disabled = true

    const pushEl = document.querySelector("[data-controller~='bridge--push-token']")
    if (!pushEl) {
      // No native bridge present (desktop web). Treat as enabled so the
      // explainer copy is shown and the flow continues naturally.
      this.notificationsGranted = true
      this.#showReminderEnabled()
      btn.disabled = false
      return
    }

    const pushController = this.application.getControllerForElementAndIdentifier(pushEl, "bridge--push-token")
    if (!pushController || typeof pushController.requestPermission !== "function") {
      this.notificationsGranted = true
      this.#showReminderEnabled()
      btn.disabled = false
      return
    }

    try {
      const result = await pushController.requestPermission()
      if (result && result.granted) {
        this.notificationsGranted = true
        this.#showReminderEnabled()
      } else {
        this.#showReminderDenied()
      }
    } catch {
      this.#showReminderDenied()
    } finally {
      btn.disabled = false
    }
  }

  submitReminder() {
    this.next()
  }

  submitReminderOptimistic() {
    this.next()
  }

  #showReminderEnabled() {
    this.reminderEnableWrapTarget.hidden = true
    this.reminderDeniedTarget.hidden = true
    if (this.hasReminderEnabledWrapTarget) {
      this.reminderEnabledWrapTarget.hidden = false
    }
  }

  #showReminderDenied() {
    this.reminderEnableWrapTarget.hidden = true
    if (this.hasReminderEnabledWrapTarget) {
      this.reminderEnabledWrapTarget.hidden = true
    }
    this.reminderDeniedTarget.hidden = false
  }

  // The paywall slide's bridge fires this once a purchase (or restore) has been
  // verified server-side. Cancelling tells the bridge not to run its own
  // navigation: onboarding finishes the flow instead, which is what flips
  // `onboarded` and releases the user from redirect_to_onboarding.
  purchaseCompleted(event) {
    event.preventDefault()
    this.goToDashboard()
  }

  async goToDashboard() {
    // `entry` tells the server whether this was a full signup or a lapsed
    // subscriber who re-entered straight at the paywall, so it can send them to
    // budget assignment or back to their dashboard respectively.
    const result = await this.postStep("complete", { entry: this.startStepValue })
    if (result.success) {
      // Onboarding finished — drop the resume cache so the next launch
      // doesn't try to restore a flow the user already completed.
      this.clearStorage()
      window.location.href = result.redirect_to
    }
  }

  currencyChanged() {
    const currency = this.currencySelectTarget.value
    const display = this.currencyMapValue[currency] || currency
    this.currencyLabelTargets.forEach(el => {
      el.textContent = display
      el.dataset.currencyCode = currency
    })
    this.refreshTierAmounts(currency)
  }

  refreshTierAmounts(currency) {
    const data = this.tierDataValue[currency]
    if (!data) return

    const tiers = ["light", "balanced", "comfortable"]
    tiers.forEach(tierKey => {
      const card = this.tierCardTargets.find(c => c.dataset.tierKey === tierKey)
      if (!card) return
      const amounts = data[tierKey]
      if (!amounts) return

      const catAmounts = card.querySelectorAll(".cat-amount")
      amounts.forEach((val, i) => {
        if (catAmounts[i]) catAmounts[i].textContent = val
      })

      const total = amounts.reduce((sum, v) => sum + v, 0)
      const tierTotalEl = card.querySelector("[data-onboarding-target='tierTotal']")
      if (tierTotalEl) tierTotalEl.textContent = total
      const btValNum = card.querySelector(".bt-val-num")
      if (btValNum) btValNum.textContent = total

      // Update +/- step deltas
      const step = data.step || 25
      card.querySelectorAll(".adjust-btn").forEach(btn => {
        const currentDelta = parseInt(btn.dataset.delta)
        btn.dataset.delta = currentDelta > 0 ? step : -step
      })
    })
  }

  // Helpers

  async postStep(step, data) {
    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        body: JSON.stringify({ step, ...data })
      })
      return await response.json()
    } catch {
      return { success: false, error: "Network error" }
    }
  }

  // Fire-and-forget POST so the slide transition isn't blocked on the network.
  // One silent retry on failure; final failure is logged (the final `complete`
  // call surfaces any persisted-state mismatch so users don't slip through
  // onboarding with missing data).
  postStepInBackground(step, data, onSuccess) {
    const attempt = (retriesLeft) => {
      this.postStep(step, data).then(result => {
        if (result?.success) {
          if (typeof onSuccess === "function") onSuccess(result)
        } else if (retriesLeft > 0) {
          setTimeout(() => attempt(retriesLeft - 1), 800)
        } else {
          console.warn(`[onboarding] step '${step}' failed to persist`, result)
        }
      })
    }
    attempt(1)
  }

  shakeChips(chips) {
    if (!chips || !chips.length) return
    const wrap = chips[0].parentElement
    if (!wrap) return
    wrap.classList.remove("shake")
    // Force reflow so the animation restarts if class was just removed
    void wrap.offsetWidth
    wrap.classList.add("shake")
    setTimeout(() => wrap.classList.remove("shake"), 500)
  }

  shakeInput(input) {
    input.style.borderColor = "#ef4444"
    input.classList.add("shake")
    setTimeout(() => {
      input.style.borderColor = ""
      input.classList.remove("shake")
    }, 600)
    input.focus()
  }

  updateDots() {
    if (!this.hasProgressFillTarget) return
    const total = Math.max(this.slideTargets.length - 1, 1)
    const pct = Math.min(Math.max(this.currentValue / total, 0), 1) * 100
    this.progressFillTarget.style.width = `${pct}%`
  }

  // Returns the localized label for the current slide's next button.
  // Falls back to the generic "Continue" label when no per-slide override exists.
  nextLabelForStep(step) {
    const overrides = this.nextLabelsValue || {}
    return overrides[String(step)] || overrides[step] || this.labelContinueValue
  }

  // The CTA is ONE persistent element, rendered by onboarding/show.html.erb and
  // mutated in place from here. It used to be re-rendered as an HTML string on
  // every step change, which destroyed the node the user was pressing: the
  // pressed state was lost and the new label painted mid-tap, so the button
  // appeared to flash into a different button under the finger.
  //
  // `deferLabel` is passed by goTo(), which writes the label later, on the same
  // beat that makes the incoming slide active.
  updateNav({ deferLabel = false } = {}) {
    const slug = this.slugForStep(this.currentValue)
    const isFirst = this.currentValue === 0
    const isLast = this.currentValue === this.totalValue - 1
    // Slides that render their own advancing controls inside the slide body,
    // so the shared bottom-nav must not draw one: auth (Apple/Google/email)
    // and paywall (plan cards + its own docked purchase sheet).
    const ownsItsFooter = slug === "auth" || slug === "paywall"
    // trial_intro is the first step after the account exists, so there is
    // nothing legitimate behind it — going back would mean re-entering a
    // questionnaire whose answers are already persisted. The paywall keeps its
    // back chevron so the user can re-read how the trial works.
    const isFirstPostAuth = slug === "trial_intro"

    // Back chevron hides on the welcome step (nothing to go back to) and on
    // the first post-auth step.
    if (this.hasBackBtnTarget) this.backBtnTarget.hidden = isFirst || isFirstPostAuth
    if (!this.hasNextBtnTarget) return

    const btn = this.nextBtnTarget
    // .onboarding .btn is display:flex, which outranks the UA [hidden] rule, so
    // onboarding.css carries an explicit `.onboarding .btn[hidden]` rule.
    btn.hidden = ownsItsFooter

    const locked = false
    btn.classList.toggle("btn-dashboard", isLast)
    btn.classList.toggle("btn-next", !isLast)
    btn.classList.toggle("is-locked", locked)
    btn.disabled = locked
    if (locked) btn.setAttribute("aria-disabled", "true")
    else btn.removeAttribute("aria-disabled")

    // The last step hands over to the dashboard instead of advancing. Stimulus
    // rebinds on the attribute change, so swapping the handler needs no new node.
    const handler = isLast ? "goToDashboard" : "submitAndNext"
    const action = `click->bridge--haptic#tap click->onboarding#${handler}`
    if (btn.dataset.action !== action) btn.dataset.action = action

    if (!deferLabel) this.applyNextLabel()
  }

  // Writes the current step's CTA label. Split out of updateNav() so goTo() can
  // hold it back until the slide transition starts (see the setTimeout there).
  applyNextLabel() {
    if (!this.hasNextLabelTarget) return

    const isFirst = this.currentValue === 0
    const isLast = this.currentValue === this.totalValue - 1
    let label
    if (isLast) label = this.labelDashboardValue
    else if (isFirst) label = this.nextLabelForStep(0) || this.labelGetStartedValue
    else label = this.nextLabelForStep(this.currentValue)

    if (this.nextLabelTarget.textContent !== label) this.nextLabelTarget.textContent = label
  }

  handleTouchStart(e) {
    // A drag that begins on a control which owns horizontal movement belongs to
    // that control, not to the slide. The budget and goal dials travel far more
    // than the 60px swipe threshold, so every real drag was also read as "next
    // slide" and the step advanced without the CTA being pressed.
    this.swipeFromControl = !!(e.target.closest &&
      e.target.closest(".amount-slider, .expense-deck, input, textarea, select"))
    this.touchStartX = e.touches[0].clientX
  }

  handleTouchEnd(e) {
    if (this.swipeFromControl) {
      this.swipeFromControl = false
      return
    }
    const diff = this.touchStartX - e.changedTouches[0].clientX
    const swipedForward = this.rtlValue ? diff < -60 : diff > 60
    if (swipedForward) this.submitAndNext()
  }

}
